import type { User } from 'oidc-client-ts';
import { useEffect, useState, type ReactElement } from 'react';

import { api } from '../../runtime.js';

/**
 * Notifications sur ce navigateur (Web Push, spec 006 US3, T058) : la clé publique VAPID est lue
 * sur `/me/devices/web-push` ; l'abonnement du navigateur est enregistré comme un téléphone
 * `web` (`PUT /me/devices`, installation stable propre au navigateur) et retiré par
 * `DELETE /me/devices/{installation}`. Section masquée tant que le coffre n'a pas les clés.
 */

const INSTALLATION_KEY = 'oasis.webpush.installation';

function installationId(): string {
  try {
    const known = window.localStorage.getItem(INSTALLATION_KEY);
    if (known !== null && known.length >= 16) return known;
    const id = `web-${crypto.randomUUID()}`;
    window.localStorage.setItem(INSTALLATION_KEY, id);
    return id;
  } catch {
    return `web-${crypto.randomUUID()}`;
  }
}

/** Clé base64 URL → octets (`applicationServerKey`). */
export function keyBytes(b64: string): Uint8Array<ArrayBuffer> {
  const s = atob(b64.replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(s.length));
  for (let i = 0; i < s.length; i += 1) out[i] = s.charCodeAt(i);
  return out;
}

const supported = (): boolean =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export function WebPushSection({ user }: { user: User }): ReactElement | null {
  const [key, setKey] = useState<string | null>(null);
  const [active, setActive] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!supported()) return;
    void api<{ enabled: boolean; publicKey: string | null }>(user, '/me/devices/web-push')
      .then(async (r) => {
        if (!r.enabled || r.publicKey === null) return;
        setKey(r.publicKey);
        const reg = await navigator.serviceWorker.ready;
        setActive((await reg.pushManager.getSubscription()) !== null);
      })
      .catch(() => undefined);
  }, [user]);

  if (key === null) return null;

  const activer = async () => {
    setMessage(null);
    try {
      if ((await Notification.requestPermission()) !== 'granted') {
        setMessage('Notifications refusées par le navigateur.');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub =
        (await reg.pushManager.getSubscription()) ??
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: keyBytes(key),
        }));
      await api(user, '/me/devices', {
        method: 'PUT',
        body: JSON.stringify({
          installationId: installationId(),
          platform: 'web',
          token: JSON.stringify(sub.toJSON()),
          environment: 'production',
          appVersion: 'web',
        }),
      });
      setActive(true);
      setMessage('Notifications activées sur ce navigateur.');
    } catch {
      setMessage('Activation impossible pour le moment.');
    }
  };

  const desactiver = async () => {
    setMessage(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      await (await reg.pushManager.getSubscription())?.unsubscribe();
      await api(user, `/me/devices/${encodeURIComponent(installationId())}`, {
        method: 'DELETE',
      }).catch(() => undefined);
      setActive(false);
      setMessage('Notifications désactivées sur ce navigateur.');
    } catch {
      setMessage('Désactivation impossible pour le moment.');
    }
  };

  return (
    <>
      <h2>Notifications sur ce navigateur</h2>
      <p>
        Recevez les recensements, directs et messages d’Oasis sur cet ordinateur ou ce téléphone,
        comme dans l’app.
      </p>
      {active ? (
        <button type="button" onClick={() => void desactiver()}>
          Désactiver sur ce navigateur
        </button>
      ) : (
        <button type="button" onClick={() => void activer()}>
          Activer sur ce navigateur
        </button>
      )}
      {message !== null && <p role="status">{message}</p>}
    </>
  );
}
